import {useEffect, useRef, useState} from "react";
import {DndContext, closestCenter, PointerSensor, useSensor, useSensors} from "@dnd-kit/core";
import {SortableContext, verticalListSortingStrategy, useSortable, arrayMove} from "@dnd-kit/sortable";
import {CSS} from "@dnd-kit/utilities";
import {toast} from "react-hot-toast";
import './Home.scss';
import {getMensagens, alternarFavoritoMensagem, salvarOrdemFavoritos} from "../../services/mensagemService";
import MensagemItem from "../../components/MensagemItem/MensagemItem";
import CadastrarMensagem from "../../components/CadastrarMensagem/CadastrarMensagem";
import TextLogo from "../../components/TextLogo/TextLogo";

function compareMessages(a: any, b: any) {
    const favoriteA = Boolean(a.favorito);
    const favoriteB = Boolean(b.favorito);

    if (favoriteA !== favoriteB) {
        return favoriteA ? -1 : 1;
    }

    const ordemA = a.ordemFavorito ?? Number.MAX_SAFE_INTEGER;
    const ordemB = b.ordemFavorito ?? Number.MAX_SAFE_INTEGER;
    if (ordemA !== ordemB) {
        return ordemA - ordemB;
    }

    return Number(b.id) - Number(a.id);
}

function sortMessages(messages: any[]) {
    return Array.from(messages).sort(compareMessages);
}

function SortableMensagemItem(props: any) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({id: props.item.id});

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 999 : 'auto' as const,
    };

    return (
        <div ref={setNodeRef} style={style}>
            <MensagemItem
                item={props.item}
                removeItemAfterDelete={props.removeItemAfterDelete}
                handleToggleFavorite={props.handleToggleFavorite}
                isPending={props.isPending}
                dragHandleListeners={listeners}
                dragHandleAttributes={attributes}
                isDragging={isDragging}
            />
        </div>
    );
}

function Home() {
    const [itemsDB, setItemsDB] = useState<any[]>([])
    const [items, setItems] = useState<any[]>([])
    const [loading, setLoading] = useState<boolean>(false)
    const [pendingFavoriteIds, setPendingFavoriteIds] = useState<Set<number>>(new Set())
    const searchInput: any = useRef(null);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        })
    );

    function getSearchValue() {
        return searchInput.current?.value ?? "";
    }

    function updateLocalMessages(updatedMessage: any) {
        setItemsDB(prevState => sortMessages(
            prevState.map((item) => item.id === updatedMessage.id ? {
                ...item,
                ...updatedMessage
            } : item)
        ));

        setItems(prevState => sortMessages(
            prevState.map((item) => item.id === updatedMessage.id ? {
                ...item,
                ...updatedMessage
            } : item)
        ));
    }

    function optimisticToggleFavorite(id: number) {
        setItemsDB(prevState => sortMessages(
            prevState.map((item) => item.id === id ? {...item, favorito: !item.favorito} : item)
        ));

        setItems(prevState => sortMessages(
            prevState.map((item) => item.id === id ? {...item, favorito: !item.favorito} : item)
        ));
    }

    useEffect(() => {
        const handleKeyDown = (evt: any) => {
            if (evt.keyCode === 70 && evt.ctrlKey) {
                evt.preventDefault()
                if(searchInput.current) {
                    searchInput.current.focus()
                }
            }
        };

        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [])

    useEffect(() => {
        const listItemsDBReq = async () => {
            setLoading(true);
            const req: any = await getMensagens();
            const sortedItems = sortMessages(Array.from(req.payload))
            setItemsDB(sortedItems)
            setItems(sortedItems)
            setLoading(false);
        }
        listItemsDBReq();
    }, [])

    function addToItemsAfterCreate(mensagem: any) {
        mensagem.new = true;
        setItemsDB(prevState => sortMessages([
            mensagem,
            ...prevState
        ]))

        if(getSearchValue().trim() === "") {
            setItems(prevState => sortMessages([
                mensagem,
                ...prevState
            ]))
        }
    }

    function removeItemAfterDelete(id: any) {
        setItemsDB(prevState => prevState.filter((item) => item.id !== id))
        setItems(prevState => prevState.filter((item) => item.id !== id))
    }

    function handleToggleFavorite(id: number, isCurrentlyFavorito: boolean) {
        setPendingFavoriteIds(prev => new Set(prev).add(id));
        optimisticToggleFavorite(id);

        toast.promise(
            alternarFavoritoMensagem(id),
            {
                loading: isCurrentlyFavorito ? 'Removendo dos favoritos...' : 'Adicionando aos favoritos...',
                success: (response) => {
                    updateLocalMessages(response.payload);
                    return response.message;
                },
                error: () => {
                    optimisticToggleFavorite(id);
                    return 'Não foi possível atualizar o favorito';
                },
            }
        ).finally(() => {
            setPendingFavoriteIds(prev => {
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
        });
    }

    function handleSearch(e: any) {
        let busca: string = e.target.value

        if(busca.trim() !== "") {
            let itemsDBCopied = sortMessages(Array.from(itemsDB).map((item: any) => ({
                ...item
            })))
            let buscaLower = busca.toLowerCase()

            let containsAll = itemsDBCopied
                .filter((item: any) => {
                    const containsTitulo = item.titulo != null && item.titulo.toLowerCase().indexOf(buscaLower) !== -1
                    const containsDescricao = item.descricao != null && item.descricao.toLowerCase().indexOf(buscaLower) !== -1
                    const containsConteudo = item.conteudo != null && item.conteudo.toLowerCase().indexOf(buscaLower) !== -1

                    return containsTitulo || containsDescricao || containsConteudo
                })
                .map((item: any) => {
                    if(item.titulo != null) {
                        let indexOf = item.titulo.toLowerCase().indexOf(buscaLower)
                        if(indexOf !== -1) {
                            let word = item.titulo.substring(indexOf, indexOf + busca.length)
                            item.titulo = item.titulo.replace(word, "<mark>"+word+"</mark>")
                        }
                    }

                    if(item.descricao != null) {
                        let indexOf = item.descricao.toLowerCase().indexOf(buscaLower)
                        if(indexOf !== -1) {
                            let word = item.descricao.substring(indexOf, indexOf + busca.length)
                            item.descricao = item.descricao.replace(word, "<mark>"+word+"</mark>")
                        }
                    }

                    if(item.conteudo != null) {
                        let indexOf = item.conteudo.toLowerCase().indexOf(buscaLower)
                        if(indexOf !== -1) {
                            let word = item.conteudo.substring(indexOf, indexOf + busca.length)
                            item.conteudo = item.conteudo.replace(word, "<mark>"+word+"</mark>")
                        }
                    }

                    return item
                })

            setItems(sortMessages(containsAll))
        } else {
            setItems(sortMessages(itemsDB))
        }
    }

    const favoritos = items.filter(item => item.favorito);
    const naoFavoritos = items.filter(item => !item.favorito);
    const favoritoIds = favoritos.map(f => f.id);

    function handleDragEnd(event: any) {
        const {active, over} = event;

        if (!over || active.id === over.id) {
            return;
        }

        setItems(prevState => {
            const favoritosList = prevState.filter(item => item.favorito);
            const naoFavoritosList = prevState.filter(item => !item.favorito);

            const oldIndex = favoritosList.findIndex(item => item.id === active.id);
            const newIndex = favoritosList.findIndex(item => item.id === over.id);

            const reorderedFavoritos = arrayMove(favoritosList, oldIndex, newIndex);

            reorderedFavoritos.forEach((item, index) => {
                item.ordemFavorito = index + 1;
            });

            const newItems = [...reorderedFavoritos, ...naoFavoritosList];
            setItemsDB(newItems);

            salvarOrdemFavoritos(reorderedFavoritos.map(item => item.id)).catch(() => {});

            return newItems;
        });
    }

    // @ts-ignore
    return (
        <div className="home">
            <div className={"header d-flex flex-column align-items-center"}>
                <div className={"content"}>
                    <div className={"title text-center mb-2"}>
                        <TextLogo/>
                        <p className={"lead mb-0"}>
                            Biblioteca
                        </p>
                    </div>
                    <div className={"search"}>
                        <div className={"input d-flex flex-column"}>
                            <input ref={searchInput} onChange={(e) => handleSearch(e)} className={"rounded-2"} placeholder={"🔎"}/>
                            <span className={"atalho text-muted"}><em>(CTRL+F)</em></span>
                        </div>
                    </div>
                </div>
            </div>

            <div className={"mt-2 content px-0 px-md-2"}>
                <div className={"d-flex justify-content-end mb-2"}>
                    <button type="button" className="btn btn-primary" data-bs-toggle="modal" data-bs-target="#exampleModal">
                        Cadastrar Mensagem
                    </button>
                </div>
                <div className={"items d-flex flex-column gap-3"}>
                    {!loading?
                        (
                            items.length !== 0?
                                (
                                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                                        <SortableContext items={favoritoIds} strategy={verticalListSortingStrategy}>
                                            {favoritos.map((item) => (
                                                <SortableMensagemItem
                                                    key={item.id}
                                                    item={item}
                                                    removeItemAfterDelete={removeItemAfterDelete}
                                                    handleToggleFavorite={handleToggleFavorite}
                                                    isPending={pendingFavoriteIds.has(item.id)}
                                                />
                                            ))}
                                        </SortableContext>
                                        {naoFavoritos.map((item) => (
                                            <MensagemItem
                                                key={item.id}
                                                item={item}
                                                removeItemAfterDelete={removeItemAfterDelete}
                                                handleToggleFavorite={handleToggleFavorite}
                                                isPending={pendingFavoriteIds.has(item.id)}
                                            />
                                        ))}
                                    </DndContext>
                                )
                            :
                                (
                                    <div className={"container-nao-encontrado d-flex flex-column align-items-center"}>
                                        <div className={"d-flex flex-column align-items-center text-center"}>
                                            <p className={"h4"}>Nada encontrado 😥</p>
                                            <p className={"h5"}>Deseja adicionar novas mensagens?</p>
                                            <button type="button" className={"btn btn-outline-success"} data-bs-toggle="modal" data-bs-target="#exampleModal">Clique aqui</button>
                                        </div>
                                    </div>
                                )
                        )
                    :
                        <div className="spinner-border text-secondary align-self-center" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    }
                </div>
            </div>
            <div className="modal fade" id="exampleModal" tabIndex={-1} aria-labelledby="exampleModalLabel"
                 aria-hidden="true">
                <div className="modal-dialog modal-lg">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h1 className="modal-title fs-5" id="exampleModalLabel">Adicionar Mensagem</h1>
                            <button id={"modal-button-close"} type="button" className="btn-close" data-bs-dismiss="modal"
                                    aria-label="Close"></button>
                        </div>
                        <div className="modal-body">
                            <CadastrarMensagem addToItemsAfterCreate={addToItemsAfterCreate} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Home;
